import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

const ForDogSchools = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  
  // Standard listing form
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Sponsored listing form
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorEmail, setSponsorEmail] = useState('');
  const [sponsorSchoolName, setSponsorSchoolName] = useState('');
  const [sponsorMessage, setSponsorMessage] = useState('');
  const [isSponsorSubmitting, setIsSponsorSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: contactName,
          email,
          schoolName,
          message,
          subject: `Listing request for ${schoolName}`
        }
      });
      
      if (error) throw error;
      
      toast({
        title: language === 'de' 
          ? 'Anfrage erfolgreich gesendet' 
          : 'Request Successfully Sent',
        description: language === 'de'
          ? 'Danke für deine Anfrage! Wir werden uns in Kürze bei dir melden.'
          : 'Thank you for your inquiry! We will get back to you shortly.',
        variant: "default",
      });
      
      // Reset form
      setContactName('');
      setEmail('');
      setSchoolName('');
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: language === 'de' ? 'Fehler' : 'Error',
        description: language === 'de' 
          ? 'Beim Senden deiner Nachricht ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.' 
          : 'An error occurred while sending your message. Please try again later.',
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSponsorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSponsorSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: sponsorName,
          email: sponsorEmail,
          schoolName: sponsorSchoolName,
          message: sponsorMessage,
          subject: `Sponsored Listing Request for ${sponsorSchoolName}`
        }
      });
      
      if (error) throw error;
      
      toast({
        title: language === 'de' 
          ? 'Anfrage erfolgreich gesendet' 
          : 'Request Successfully Sent',
        description: language === 'de'
          ? 'Danke für deine Anfrage! Wir werden uns in Kürze bei dir melden.'
          : 'Thank you for your inquiry! We will get back to you shortly.',
        variant: "default",
      });
      
      // Reset form
      setSponsorName('');
      setSponsorEmail('');
      setSponsorSchoolName('');
      setSponsorMessage('');
    } catch (error) {
      console.error('Error sending sponsor message:', error);
      toast({
        title: language === 'de' ? 'Fehler' : 'Error',
        description: language === 'de' 
          ? 'Beim Senden deiner Nachricht ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.' 
          : 'An error occurred while sending your message. Please try again later.',
        variant: "destructive",
      });
    } finally {
      setIsSponsorSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/" className="inline-flex items-center text-primary hover:underline mb-6">
            <ChevronLeft size={20} />
            <span>{language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}</span>
          </Link>

          <h1 className="text-3xl font-bold mb-8 text-gray-900">
            {language === 'de' ? 'Für Hundeschulen' : 'For Dog Schools'}
          </h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">
                {language === 'de' ? 'Listeneintrag hinzufügen oder entfernen' : 'Add or Remove Listing'}
              </h2>
              <p className="text-gray-600 mb-6">
                {language === 'de' 
                  ? 'Du möchtest deine Hundeschule in unserem Verzeichnis hinzufügen oder einen bestehenden Eintrag ändern? Fülle einfach das untenstehende Formular aus und wir kümmern uns darum!' 
                  : 'Would you like to add your dog school to our directory or modify an existing entry? Simply fill out the form below and we\'ll take care of it!'}
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'de' ? 'Kontaktperson' : 'Contact Person'}
                  </label>
                  <Input
                    type="text"
                    id="contactName"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'de' ? 'E-Mail-Adresse' : 'Email Address'}
                  </label>
                  <Input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="schoolName" className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'de' ? 'Name der Hundeschule' : 'Dog School Name'}
                  </label>
                  <Input
                    type="text"
                    id="schoolName"
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'de' ? 'Deine Nachricht' : 'Your Message'}
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md h-32"
                    disabled={isSubmitting}
                    required
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      {language === 'de' ? 'Wird gesendet...' : 'Sending...'}
                    </>
                  ) : (
                    language === 'de' ? 'Absenden' : 'Submit'
                  )}
                </Button>
              </form>
            </Card>
            
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">
                {language === 'de' ? 'Sponsored Listings' : 'Sponsored Listings'}
              </h2>
              <p className="text-gray-600 mb-6">
                {language === 'de' 
                  ? 'Erhöhe die Sichtbarkeit deiner Hundeschule mit unseren Sponsored Listings! Kontaktiere uns für weitere Informationen.' 
                  : 'Increase the visibility of your dog school with our sponsored listings! Contact us for more information.'}
              </p>
              
              <form onSubmit={handleSponsorSubmit} className="space-y-4">
                <div>
                  <label htmlFor="sponsorName" className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'de' ? 'Kontaktperson' : 'Contact Person'}
                  </label>
                  <Input
                    type="text"
                    id="sponsorName"
                    value={sponsorName}
                    onChange={(e) => setSponsorName(e.target.value)}
                    disabled={isSponsorSubmitting}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="sponsorEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'de' ? 'E-Mail-Adresse' : 'Email Address'}
                  </label>
                  <Input
                    type="email"
                    id="sponsorEmail"
                    value={sponsorEmail}
                    onChange={(e) => setSponsorEmail(e.target.value)}
                    disabled={isSponsorSubmitting}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="sponsorSchoolName" className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'de' ? 'Name der Hundeschule' : 'Dog School Name'}
                  </label>
                  <Input
                    type="text"
                    id="sponsorSchoolName"
                    value={sponsorSchoolName}
                    onChange={(e) => setSponsorSchoolName(e.target.value)}
                    disabled={isSponsorSubmitting}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="sponsorMessage" className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'de' ? 'Deine Nachricht' : 'Your Message'}
                  </label>
                  <textarea
                    id="sponsorMessage"
                    value={sponsorMessage}
                    onChange={(e) => setSponsorMessage(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md h-32"
                    disabled={isSponsorSubmitting}
                    required
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={isSponsorSubmitting}>
                  {isSponsorSubmitting ? (
                    <>
                      {language === 'de' ? 'Wird gesendet...' : 'Sending...'}
                    </>
                  ) : (
                    language === 'de' ? 'Jetzt anfragen' : 'Inquire Now'
                  )}
                </Button>
              </form>
            </Card>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default ForDogSchools;
